/**
 * Tenant Settings Service
 * Story: DASH-7.0.3
 *
 * Manages tenant-wide configuration settings with caching.
 * Provides methods to fetch and update tenant settings like revenue goals,
 * display preferences, and widget defaults.
 */

import { clearCommissionCostingEnabledCache } from '@/lib/commissionCostingEnabledCache';
import { supabase } from '@/lib/supabaseClient';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import type { TenantSettings, UpdateTenantSettingsInput } from '@/types/tenant';

// In-memory cache structure
interface CacheEntry {
  data: TenantSettings;
  timestamp: number;
}

// Cache storage
const settingsCache = new Map<string, CacheEntry>();

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Get tenant settings with caching
 * @returns Promise<TenantSettings | null> - Tenant settings or null if not found
 */
export async function getTenantSettings(): Promise<TenantSettings | null> {
  try {
    const tenantId = await getCurrentTenantId();
    if (!tenantId) {
      console.error('[tenantSettingsService] No tenant ID available');
      return null;
    }

    // Check cache
    const cached = settingsCache.get(tenantId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }

    // Fetch from database
    const { data, error } = await supabase
      .from('tenant_settings')
      .select('*')
      .eq('tenant_id', tenantId)
      .single();

    if (error) {
      console.error('[tenantSettingsService] Failed to fetch tenant settings:', error);
      return null;
    }

    // Cache result
    if (data) {
      settingsCache.set(tenantId, {
        data,
        timestamp: Date.now(),
      });
    }

    return data;
  } catch (error) {
    console.error('[tenantSettingsService] Error in getTenantSettings:', error);
    return null;
  }
}

/**
 * Get settings for an explicitly named tenant, sharing the same cache.
 *
 * For callers that already hold a tenant id and must not resolve a different one
 * (e.g. lead routing, which decides per lead's tenant). RLS still limits what is
 * readable, so this cannot be used to read another tenant's settings.
 *
 * @param tenantId - The tenant whose settings to read
 * @returns Promise<TenantSettings | null> - Settings, or null if unavailable
 */
export async function getTenantSettingsById(
  tenantId: string
): Promise<TenantSettings | null> {
  try {
    if (!tenantId) return null;

    const cached = settingsCache.get(tenantId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }

    const { data, error } = await supabase
      .from('tenant_settings')
      .select('*')
      .eq('tenant_id', tenantId)
      .single();

    if (error) {
      console.error('[tenantSettingsService] Failed to fetch settings for tenant:', error);
      return null;
    }

    if (data) {
      settingsCache.set(tenantId, { data, timestamp: Date.now() });
    }

    return data;
  } catch (error) {
    console.error('[tenantSettingsService] Error in getTenantSettingsById:', error);
    return null;
  }
}

/**
 * Update tenant settings
 * @param updates - Partial settings object with fields to update
 * @returns Promise<TenantSettings | null> - Updated settings or null on error
 * @throws Error if update fails
 */
export async function updateTenantSettings(
  updates: UpdateTenantSettingsInput
): Promise<TenantSettings | null> {
  try {
    const tenantId = await getCurrentTenantId();
    if (!tenantId) {
      throw new Error('No tenant ID available');
    }

    const { data, error } = await supabase
      .from('tenant_settings')
      .update(updates)
      .eq('tenant_id', tenantId)
      .select()
      .single();

    if (error) {
      // W1R P14: zero rows matched means this tenant has no settings row.
      // `.single()`'s raw PGRST116 text ("JSON object requested, multiple (or
      // no) rows returned") reached the UI verbatim while the write was
      // silently a no-op. Name what actually happened instead.
      if (error.code === 'PGRST116') {
        throw new Error(
          'This account has no settings row, so nothing was changed. Create its tenant_settings row first, then retry.'
        );
      }
      console.error('[tenantSettingsService] Failed to update tenant settings:', error);
      throw error;
    }

    // Invalidate cache on successful update
    settingsCache.delete(tenantId);
    clearCommissionCostingEnabledCache(tenantId);

    return data;
  } catch (error) {
    console.error('[tenantSettingsService] Error in updateTenantSettings:', error);
    throw error;
  }
}

/**
 * Clear cache for tenant settings
 * Useful after updates or for forcing a fresh fetch
 * @param tenantId - Optional tenant ID to clear specific cache entry
 */
export function clearTenantSettingsCache(tenantId?: string): void {
  if (tenantId) {
    settingsCache.delete(tenantId);
  } else {
    settingsCache.clear();
  }
  clearCommissionCostingEnabledCache(tenantId);
}

/**
 * Get cache statistics for debugging
 * @returns Object with cache statistics
 */
export function getTenantSettingsCacheStats() {
  const now = Date.now();
  const entries = Array.from(settingsCache.entries());
  const validEntries = entries.filter(
    ([_, value]) => now - value.timestamp < CACHE_TTL
  );

  return {
    totalEntries: entries.length,
    validEntries: validEntries.length,
    expiredEntries: entries.length - validEntries.length,
    cacheTtlMs: CACHE_TTL,
  };
}
